/**
 * The Inbox inspector: the decision view for decisions (entity or legacy question), the batch
 * view for a decision session, and the item's own card for everything else (approvals,
 * landings, releases, proposals, builds). A right pane on desktop, a full-screen push view on
 * mobile (the Split layout), never a bottom sheet.
 */
import type { EventOf } from '@viko/protocol'
import { type Answers, InboxCard, LandCard } from '@viko/transcript-ui'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import type { ActionEntity } from '../../lib/actions.js'
import { isBlockerItem, isLandingFailure } from '../../lib/inbox-freshness.js'
import type { InboxRow } from '../../lib/now-inbox.js'
import type { BuildCard, InboxItem } from '../../lib/types.js'
import { BatchView } from '../decision/BatchView.js'
import { DecisionPanel, parseDecisionKey } from '../decision/DecisionPanel.js'
import { BuildView, FeedbackSheet, HubCard } from '../HubCards.js'
import { InspectorFrame } from '../inspector/InspectorFrame.js'
import { landSteps } from '../LandSheet.js'
import { Empty, SkeletonRows } from '../ui.js'
import { AlertDetail, BlockerDetail, LandingFailureDetail } from './ItemDetail.js'
import { useAskActions } from './useAskActions.js'
import { useInboxRows } from './useInboxRows.js'
import './triage.css'

export function InboxInspector({
  inspectKey,
  intent,
  onClose,
  onDone,
  backLabel = 'Back to Inbox',
  position,
  onStep,
  extra,
}: {
  inspectKey: string
  intent?: 'reply' | 'delegate'
  onClose: () => void
  /** Mobile back button (the list the inspector was opened from). */
  backLabel?: string
  /** After an answer: the list decides what opens next. */
  onDone?: () => void
  /** app-clarity: "1 of 11" and the next item's title; j/k (and a swipe) step through. */
  position?: { at: number; total: number; next?: string | undefined; label?: string }
  onStep?: (dir: 1 | -1) => void
  extra?: ReactNode
}) {
  const heading = useRef<HTMLHeadingElement>(null)
  const built = useInboxRows()
  const { kind, id } = parseDecisionKey(inspectKey)
  const row =
    built.rows.find((r) => r.key === inspectKey) ?? built.snoozed.find((r) => r.key === inspectKey)
  // biome-ignore lint/correctness/useExhaustiveDependencies: focus the heading per item
  useEffect(() => {
    heading.current?.focus()
  }, [inspectKey])

  let title: string = row?.title ?? 'Decision'
  let body: ReactNode
  if (kind === 'batch') {
    const rows = built.rows.filter(
      (r) => r.kind === 'decision' && (r.workstreamId ?? r.view?.runId) === id,
    )
    title = rows[0] ? `${rows[0].projectName ?? 'Decisions'}: decide all` : 'Decide all'
    body = rows.length ? (
      <BatchView
        title={`${rows.length} decisions${rows[0]?.lane ? ` in ${rows[0].lane}` : ''}`}
        keys={rows.map((r) => r.key)}
        {...(onDone ? { onDone } : {})}
      />
    ) : (
      <Empty center title="Nothing left to decide here">
        Every decision in this session is answered.
      </Empty>
    )
  } else if (kind === 'decision' || (kind === 'ask' && (!row || row.kind === 'decision'))) {
    if (kind === 'decision' && !row) {
      const d = built.decisions.data?.find((x) => x.id === id)
      if (d) title = d.title ?? d.question
    }
    body =
      row?.view?.multiQuestion && row.item ? (
        <div className="tr-detail__card">
          <LegacyQuestionCard item={row.item} onDone={onDone} />
        </div>
      ) : (
        <DecisionPanel
          inspectKey={inspectKey}
          {...(intent ? { intent } : {})}
          {...(onDone ? { onDone } : {})}
        />
      )
  } else if (row?.item) {
    body = <ItemDetailFor row={row} item={row.item} onDone={onDone} />
  } else
    body = built.loading ? (
      <SkeletonRows n={4} />
    ) : (
      <Empty center title="Nothing to do here">
        It was answered, snoozed or resolved on another device.
      </Empty>
    )

  // The registry footer's entity for kinds that are not decisions (a decision publishes its own).
  const item = row?.item
  const entity: ActionEntity | undefined =
    kind === 'decision' || kind === 'batch' || kind === 'ask' || !item
      ? undefined
      : item.type === 'land' && item.land
        ? {
            kind: 'landing',
            id: item.land.workstreamId ?? item.id,
            title,
            ...(item.land.branch ? { branch: item.land.branch } : {}),
            ...((item.machineId ?? (item.body as { machineId?: string } | null)?.machineId)
              ? {
                  machineId: (item.machineId ??
                    (item.body as { machineId?: string }).machineId) as string,
                }
              : {}),
            workstreamId: item.land.workstreamId,
            ...(item.runId ? { runId: item.runId } : {}),
          }
        : item.type === 'build' && (item.body as BuildCard | null)?.buildId
          ? { kind: 'build', id: (item.body as BuildCard).buildId, title }
          : undefined
  return (
    <InspectorFrame
      skin="tr"
      kind={kind}
      title={title}
      onClose={onClose}
      backLabel={backLabel}
      headingRef={heading}
      entity={entity}
      {...(kind === 'decision' || kind === 'ask' ? { kicker: 'Decision' } : {})}
      {...(position ? { position } : {})}
      {...(onStep ? { onStep } : {})}
      {...(extra ? { extra } : {})}
      {...(row?.projectId ? { projectId: row.projectId } : {})}
    >
      {body}
    </InspectorFrame>
  )
}

/** A legacy event with several questions: the classic card answers them together. */
function LegacyQuestionCard({
  item,
  onDone,
}: {
  item: InboxItem
  onDone?: (() => void) | undefined
}) {
  const ask = useAskActions()
  const ev = item.event as EventOf<'question'>
  return (
    <InboxCard
      id={item.id}
      kind="question"
      project={item.projectName ?? 'Project'}
      workstream={item.workstreamName ?? ''}
      agent={item.agent ?? 'claude'}
      waitingSince={item.createdAt}
      questions={ev.questions}
      onAnswer={(a: Answers) => void ask.answer(item, a).then((ok) => ok && onDone?.())}
      onSnooze={() =>
        void ask.snooze(item, Date.now() + 2 * 3600_000).then((ok) => ok && onDone?.())
      }
    />
  )
}

/** inbox-detail: the underlying content for each kind; other kinds keep their own card. */
export function ItemDetailFor({
  row,
  item,
  onDone,
}: {
  row: Pick<InboxRow, 'freshness' | 'title' | 'projectName'>
  item: InboxItem
  onDone?: (() => void) | undefined
}) {
  if (isBlockerItem(item))
    return <BlockerDetail item={item} freshness={row.freshness} onDone={onDone} />
  if (isLandingFailure(item))
    return <LandingFailureDetail item={item} freshness={row.freshness} onDone={onDone} />
  if (item.category === 'device-alert') return <AlertDetail item={item} onDone={onDone} />
  return (
    <div className="tr-detail__card">
      <ItemCard row={row} item={item} onDone={onDone} />
    </div>
  )
}

function ItemCard({
  row,
  item,
  onDone,
}: {
  row: Pick<InboxRow, 'title' | 'projectName'>
  item: InboxItem
  onDone?: (() => void) | undefined
}) {
  const ask = useAskActions()
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const snooze = () =>
    void ask.snooze(item, Date.now() + 2 * 3600_000).then((ok) => ok && onDone?.())
  if (item.type === 'approval' && item.event?.kind === 'approval') {
    const e = item.event
    return (
      <InboxCard
        id={item.id}
        kind="approval"
        project={item.projectName ?? 'Project'}
        workstream={item.workstreamName ?? ''}
        agent={item.agent ?? 'claude'}
        waitingSince={item.createdAt}
        approval={{
          approvalKind: e.approvalKind,
          state: 'awaiting-user',
          ...(e.title ? { title: e.title } : {}),
          ...(e.command ? { command: e.command } : {}),
          ...(e.cwd ? { cwd: e.cwd } : {}),
          ...(e.reason ? { reason: e.reason } : {}),
          diffs: e.diffs ?? [],
          ...(e.plan ? { plan: e.plan } : {}),
          ...(e.toolName ? { toolName: e.toolName } : {}),
          ...(e.input !== undefined ? { input: e.input } : {}),
        }}
        onDecide={(d, m) => void ask.decide(item, d, m).then((ok) => ok && onDone?.())}
        onSnooze={snooze}
      />
    )
  }
  if (item.type === 'land' && item.land && item.category !== 'ship-tap')
    return (
      <LandCard
        branch={item.land.branch || 'branch'}
        title={item.workstreamName ?? row.title}
        project={item.projectName ?? row.projectName ?? 'Project'}
        state="awaiting-user"
        steps={landSteps({ state: 'ready' }, 0)}
        commits={item.land.commits}
        files={item.land.files}
        additions={item.land.additions}
        deletions={item.land.deletions}
        tasks={item.land.tasks}
        requiresPasskey
        onLand={() => void ask.land(item).then((ok) => ok && onDone?.())}
      />
    )
  if (item.derived && item.type === 'build' && (item.body as BuildCard | null)?.buildId) {
    const card = item.body as BuildCard
    return (
      <>
        <BuildView
          card={card}
          {...(item.projectName ? { project: item.projectName } : {})}
          onFeedback={() => setFeedback(card)}
        />
        {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
      </>
    )
  }
  return (
    <HubCard
      item={item}
      resolve={(ids) => {
        ask.resolveLocal(ids)
        onDone?.()
      }}
      onSnooze={snooze}
    />
  )
}
